<script setup>
import { computed, reactive, watch } from 'vue';
import SettingsAccordion from 'dashboard/components-next/Settings/SettingsAccordion.vue';
import SettingsToggleSection from 'dashboard/components-next/Settings/SettingsToggleSection.vue';
import SettingsFieldSection from 'dashboard/components-next/Settings/SettingsFieldSection.vue';
import Checkbox from 'dashboard/components-next/checkbox/Checkbox.vue';

const props = defineProps({
  config: {
    type: Object,
    default: () => ({}),
  },
  isApiInbox: {
    type: Boolean,
    default: false,
  },
  isFeatureEnabled: {
    type: Boolean,
    default: false,
  },
});

const form = reactive({
  emailOnConversationCreated: false,
  emailOnConversationResolved: false,
  emailOnInactivity: false,
  inactivityEmailIntervalMinutes: 120,
});

const normalizedInterval = computed(() => {
  const value = Number(form.inactivityEmailIntervalMinutes || 120);
  return Math.min(Math.max(value, 30), 1440);
});

const syncConfig = () => {
  form.emailOnConversationCreated =
    props.config?.email_on_conversation_created || false;
  form.emailOnConversationResolved =
    props.config?.email_on_conversation_resolved || false;
  form.emailOnInactivity = props.config?.email_on_inactivity || false;
  form.inactivityEmailIntervalMinutes =
    props.config?.inactivity_email_interval_minutes || 120;
};

watch(() => props.config, syncConfig, { immediate: true, deep: true });

const getPayload = () => ({
  id: props.config?.id,
  email_on_conversation_created: form.emailOnConversationCreated,
  email_on_conversation_resolved: form.emailOnConversationResolved,
  email_on_inactivity: form.emailOnInactivity,
  inactivity_email_interval_minutes: normalizedInterval.value,
});

defineExpose({ getPayload });
</script>

<template>
  <SettingsAccordion
    :title="$t('INBOX_MGMT.SETTINGS_POPUP.EMAIL_NOTIFICATIONS.TITLE')"
    class="mt-6"
  >
    <div class="flex flex-col gap-4 py-4">
      <p class="text-sm text-n-slate-11 m-0">
        {{
          isApiInbox
            ? $t(
                'INBOX_MGMT.SETTINGS_POPUP.EMAIL_NOTIFICATIONS.DESCRIPTION_API'
              )
            : $t(
                'INBOX_MGMT.SETTINGS_POPUP.EMAIL_NOTIFICATIONS.DESCRIPTION_WEB_WIDGET'
              )
        }}
      </p>
      <p v-if="!isFeatureEnabled" class="text-sm text-n-amber-11 m-0">
        {{
          $t('INBOX_MGMT.SETTINGS_POPUP.EMAIL_NOTIFICATIONS.FEATURE_DISABLED')
        }}
      </p>

      <SettingsToggleSection
        v-model="form.emailOnConversationCreated"
        :header="$t('INBOX_MGMT.SETTINGS_POPUP.EMAIL_NOTIFICATIONS.ON_CREATED')"
        :description="
          $t('INBOX_MGMT.SETTINGS_POPUP.EMAIL_NOTIFICATIONS.ON_CREATED_HELP')
        "
        hide-toggle
        :class="{ 'opacity-50': !isFeatureEnabled }"
      >
        <template #editor>
          <label class="flex gap-2 items-start cursor-pointer">
            <Checkbox
              v-model="form.emailOnConversationCreated"
              :disabled="!isFeatureEnabled"
            />
            <span class="text-sm text-n-slate-12">
              {{
                $t('INBOX_MGMT.SETTINGS_POPUP.EMAIL_NOTIFICATIONS.ON_CREATED')
              }}
            </span>
          </label>
        </template>
      </SettingsToggleSection>

      <SettingsToggleSection
        v-model="form.emailOnInactivity"
        :header="
          $t('INBOX_MGMT.SETTINGS_POPUP.EMAIL_NOTIFICATIONS.ON_INACTIVITY')
        "
        :description="
          $t('INBOX_MGMT.SETTINGS_POPUP.EMAIL_NOTIFICATIONS.ON_INACTIVITY_HELP')
        "
        hide-toggle
        :class="{ 'opacity-50': !isFeatureEnabled }"
      >
        <template #editor>
          <label class="flex gap-2 items-start cursor-pointer">
            <Checkbox
              v-model="form.emailOnInactivity"
              :disabled="!isFeatureEnabled"
            />
            <span class="text-sm text-n-slate-12">
              {{
                $t(
                  'INBOX_MGMT.SETTINGS_POPUP.EMAIL_NOTIFICATIONS.ON_INACTIVITY'
                )
              }}
            </span>
          </label>
        </template>
      </SettingsToggleSection>

      <SettingsFieldSection
        v-if="form.emailOnInactivity"
        :label="
          $t(
            'INBOX_MGMT.SETTINGS_POPUP.EMAIL_NOTIFICATIONS.INACTIVITY_INTERVAL'
          )
        "
        :help-text="
          $t(
            'INBOX_MGMT.SETTINGS_POPUP.EMAIL_NOTIFICATIONS.INACTIVITY_INTERVAL_HINT'
          )
        "
      >
        <input
          v-model.number="form.inactivityEmailIntervalMinutes"
          type="number"
          min="30"
          max="1440"
          step="15"
          :disabled="!isFeatureEnabled"
          class="!mb-0 max-w-40"
        />
      </SettingsFieldSection>

      <SettingsToggleSection
        v-model="form.emailOnConversationResolved"
        :header="
          $t('INBOX_MGMT.SETTINGS_POPUP.EMAIL_NOTIFICATIONS.ON_RESOLVED')
        "
        :description="
          $t('INBOX_MGMT.SETTINGS_POPUP.EMAIL_NOTIFICATIONS.ON_RESOLVED_HELP')
        "
        hide-toggle
        :class="{ 'opacity-50': !isFeatureEnabled }"
      >
        <template #editor>
          <label class="flex gap-2 items-start cursor-pointer">
            <Checkbox
              v-model="form.emailOnConversationResolved"
              :disabled="!isFeatureEnabled"
            />
            <span class="text-sm text-n-slate-12">
              {{
                $t('INBOX_MGMT.SETTINGS_POPUP.EMAIL_NOTIFICATIONS.ON_RESOLVED')
              }}
            </span>
          </label>
        </template>
      </SettingsToggleSection>
    </div>
  </SettingsAccordion>
</template>
